import type { Account } from '@lyrmo/shared';
import { useCountingNumber } from '../../hooks/useCountingNumber';
import { useI18n } from '../../i18n/i18n-context';
import { usageLevel } from './usage-level';

/**
 * Today's credits, in the account menu and the credits breakdown: the meter shows what's left.
 * Made as the panel opens, so the counts run up to their values, and the meter fills with them;
 * spent meanwhile, they count on to the new ones, the meter emptying.
 */
export function UsageSummary({ usage }: { readonly usage: Account['usage'] }) {
  const { t, format } = useI18n();
  const used = useCountingNumber(usage.used);
  const left = useCountingNumber(usage.remaining);
  return (
    <section className="account-usage" aria-label={t.account.todaysCredits}>
      <p className="account-usage-row">
        <span>{t.account.todaysCredits}</span>
        <span>
          <strong>{format.number(left)}</strong> / {format.number(usage.limit)}
        </span>
      </p>
      <progress
        className="account-meter"
        data-level={usageLevel(usage)}
        value={Math.min(left, usage.limit)}
        max={usage.limit || 1}
        aria-label={t.account.creditsLeft}
      />
      <p className="account-usage-caption">
        <span>{t.account.usedLeft(format.number(used), format.number(left))}</span>
        <span>{t.account.resetsAt(format.resetTime(usage.resetsAt))}</span>
      </p>
    </section>
  );
}
